import { useEffect, useState } from "react";
import { Mail, Power, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { AdminBroadcasts } from "../components/admin/AdminBroadcasts";

const fmt = (ts) => (ts ? new Date(ts).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminAutomatedEmails() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/email-triggers").then((r) => setRows(r.data.triggers)).catch(() => toast.error("Couldn't load email triggers"));
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    setBusy(t.key);
    try {
      const { data } = await api.put(`/admin/email-triggers/${t.key}`, { enabled: !t.enabled });
      setRows(data.triggers);
      toast.success(`${t.name} ${t.enabled ? "paused" : "enabled"}`);
    } catch (_e) { toast.error("Couldn't update"); }
    finally { setBusy(""); }
  };

  return (
    <AdminShell current="/admin/automated-emails">
      <AdminBroadcasts />
      <div className="mb-5 flex items-start gap-3" data-testid="automated-emails-page">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A8B85]/15 text-[#7BD3CE]"><Mail className="h-5 w-5" /></span>
        <div>
          <h1 className="text-xl font-extrabold text-white">Automated founder emails</h1>
          <p className="text-sm text-neutral-400">Every automated email in the founder journey, in order. Pause any trigger here — paused emails are skipped (not queued) until re-enabled. All send from The Clinq Team &lt;noreply@clinq.africa&gt;.</p>
        </div>
      </div>
      {!rows ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-800">
          <table className="w-full text-xs text-neutral-300">
            <thead className="bg-neutral-900 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-400">
              <tr>
                <th className="px-3 py-2">#</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Trigger</th>
                <th className="px-3 py-2">Sent (total)</th><th className="px-3 py-2">Last sent</th><th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t, i) => (
                <tr key={t.key} className="border-t border-neutral-800" data-testid={`email-trigger-${t.key}`}>
                  <td className="px-3 py-2 text-neutral-500">{i + 1}</td>
                  <td className="px-3 py-2 font-semibold text-white">{t.name}</td>
                  <td className="px-3 py-2 text-neutral-400">{t.trigger}</td>
                  <td className="px-3 py-2 tabular-nums">{t.sent_total}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(t.last_sent_at)}</td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => toggle(t)} disabled={busy === t.key} data-testid={`email-trigger-toggle-${t.key}`}
                      className={"inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold transition-colors " +
                        (t.enabled ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" : "bg-amber-500/15 text-amber-300 hover:bg-amber-500/25")}>
                      <Power className="h-3 w-3" /> {t.enabled ? "Enabled" : "Paused"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
